<script setup lang="ts">
import {IChatMessage} from "@/features/personal/personal.types";
import {DateTime} from "luxon";
import VoiceMessage from "@/features/message/ui/voiceMessage.vue";
import {Image01, File06, DotsVertical} from "utruckin-icons";
import CallMessage from "@/features/message/ui/callMessage.vue";
import VideoMessage from "@/features/message/ui/videoMessage.vue";
import {IMessageHistory} from "@/types/main";
import {ref} from "vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonButton from "@/components/common/CommonButton.vue";

interface IPersonalChat {
  item: IChatMessage
}

const props = withDefaults(defineProps<IPersonalChat>(), {});

const historyModal = ref<boolean>(false);
const messageHistory = ref<IMessageHistory[]>([])

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

const formatDate = (value: string): string => {
  return DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss', {
    zone: 'utc',
  })
      .setZone('America/New_York')
      .toFormat('HH:mm')
}

const imageClick = (item: any, event: MouseEvent) => {
  event.stopPropagation()
  window.open(`${apiBaseUrl}${item.media_path}`, "_blank");
}

const showHistory = () => {
  let newHistory: IMessageHistory[] = [...props.item.history];
  newHistory.push({
    id: props.item.message_id,
    edited_at: props.item.activity_time,
    old_text: props.item.text,
  })

  const lastEditedAt = newHistory[newHistory.length - 1].edited_at;
  for (let i = newHistory.length - 1; i > 0; i--) {
    newHistory[i].edited_at = newHistory[i - 1].edited_at;
  }

  newHistory[0].edited_at = lastEditedAt;

  console.log(newHistory);

  historyModal.value = true;
  messageHistory.value = newHistory;
}

const closeModal = () => {
  messageHistory.value = []
}

const closeHistoryModal = () => {
  historyModal.value = false;
  closeModal();
}

const formatHistoryDate = (value: string) => {
  let newValue;
  if (value.includes("T")) {
    newValue = DateTime.fromISO(value, {
      zone: 'utc',
    })
        .setZone('America/New_York')
        .toFormat('yyyy-MM-dd HH:mm:ss')
  } else {
    newValue = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss', {
      zone: 'utc',
    })
        .setZone('America/New_York')
        .toFormat('yyyy-MM-dd HH:mm:ss')
  }

  let time: string;

  time = newValue.split(" ")[1];

  return time.split(":")[0] + ":" + time.split(":")[1] + ":" + time.split(":")[2];
}

</script>

<template>
  <div class="w-ui-personal-chat" :class="item.is_outgoing ? '' : 'left'">
    <div v-if="!item.is_outgoing" class="time">{{ formatDate(item.activity_time) }}</div>
    <div v-if="item.is_outgoing && item.is_edited" class="additional" @click="showHistory">
      <DotsVertical :size="16"/>
    </div>
    <div class="message" :class="item.is_outgoing ? '' : 'left'">
      <div class="audio" v-if="item.msg_type == 'audio'">
        <VoiceMessage :audio-url="`${apiBaseUrl}${item.media_path}`"/>
      </div>

      <div class="photo" v-if="item.msg_type == 'photo'">
        <div class="photo__action">
          <div class="picture" @click="(e: MouseEvent) => imageClick(item, e)">
            <Image01 :size="32"/>
          </div>
        </div>
      </div>

      <div class="document" v-if="item.msg_type == 'document'">
        <div class="document__action">
          <div class="document" @click="(e: MouseEvent) => imageClick(item, e)">
            <File06 :size="32"/>
          </div>
        </div>
      </div>

      <div class="video" v-if="item.msg_type == 'video'">
        <VideoMessage :document-url="`${apiBaseUrl}${item.media_path}`"/>
      </div>

      <div class="call" v-if="item.item_type == 'call'">
        <CallMessage :item="item"/>
      </div>

      <div v-if="item.text" class="text">
        {{ item.text }}
      </div>
    </div>
    <div v-if="item.is_outgoing" class="time">{{ formatDate(item.activity_time) }}</div>
    <div v-if="!item.is_outgoing && item.is_edited" class="additional" @click="showHistory">
      <DotsVertical :size="16"/>
    </div>
  </div>

  <CommonModal
      v-model="historyModal"
      @close="closeModal"
  >
    <div class="history">
      <div class="history__item" v-for="(item, index) in messageHistory" :key="index">
        <div class="history__item--date">{{ formatHistoryDate(item.edited_at) }}</div>
        <div class="history__item--message" lang="en">{{ item.old_text }}</div>
      </div>
    </div>

    <template #footer>
      <div class="modal__footer">
        <CommonButton
            type="primary"
            label="Close"
            @clicked="closeHistoryModal"
        />
      </div>
    </template>
  </CommonModal>
</template>

<style scoped lang="scss">
.w-ui-personal-chat {
  margin-top: 8px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;

  &.left {
    justify-content: flex-start;
  }

  .time {
    color: var(--color-secondary-label);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0;
  }

  .additional {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 1px solid var(--color-border);
    position: relative;
    z-index: 1;
    cursor: pointer;
    color: var(--color-label);

    &:hover {
      background-color: var(--color-bg-secondary);
    }
  }

  .message {
    max-width: 40%;
    padding: 12px;
    border-radius: 16px;
    background: var(--color-secondary-label);
    color: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: 8px;

    &.left {
      background: var(--color-bg-tertiary);
      border: 1px solid var(--color-border);
      color: var(--color-label);
    }

    .audio, .text, .video, .call {
      background: transparent;
      overflow-wrap: break-word;
      word-break: break-word;
      hyphens: auto;
    }

    .photo, .document {
      background: transparent;

      &__action {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        background: transparent;

        .picture, .document {
          background: var(--color-background);
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          border: 1px solid var(--color-border);
          cursor: pointer;
          color: var(--color-label);
        }
      }
    }
  }
}
</style>